import { canonicalHash, CANON_V2 } from './canon.ts';
import { hexToBytes } from './bytes.ts';
import { lookupCcdScan, lookupWalletProxy, explorerUrl, type ChainTx, type Fetch } from './chain.ts';
import { findKey, type IssuerDocument } from './issuer.ts';
import { isArticle, openPayload, parseEnvelope, verifyEnvelope, type NetIdPacket } from './packet.ts';
import { decodeRecord, type ProvenanceRecord } from './record.ts';

/** `live` is added by the scanner (the liveness rule, liveness.ts) once it has seen enough packets. */
export type CheckKey = 'signature' | 'fresh' | 'live' | 'copy' | 'hash' | 'chain' | 'record' | 'sender' | 'second-source';

export type Check = { key: CheckKey; label: string; ok: boolean; required: boolean; detail: string };

export type Verification = {
  genuine: boolean;
  checks: Check[];
  packet: NetIdPacket | null;
  record: ProvenanceRecord | null;
  tx: ChainTx | null;
  explorer: string | null;
  /** The issuer's published document and the key that verified the signature (for the company checks). */
  issuerDoc?: IssuerDocument | null;
  signingKey?: Uint8Array | null;
  error?: string;
};

export type VerifyOptions = {
  /** Returns the issuer's published document (well-known file). */
  resolveIssuer: (issuer: string) => Promise<IssuerDocument>;
  fetch: Fetch;
  now?: number;
  /** Seconds a packet stays fresh (SPEC.md §5). */
  maxAgeSec?: number;
  /** Seconds a packet's iat may be ahead of this clock. */
  maxSkewSec?: number;
};

/** Every code needs these; an article code also hash, chain, record and sender (each added as required). */
const ALWAYS: CheckKey[] = ['signature', 'fresh'];

export const DEFAULT_MAX_AGE_SEC = 30;
export const DEFAULT_MAX_SKEW_SEC = 5;

const LABELS: Record<CheckKey, string> = {
  signature: 'Signed by the issuer',
  fresh: 'Fresh code',
  live: 'Live, not a recording',
  copy: 'Not a copy shown to others',
  hash: 'Text matches its fingerprint',
  chain: 'Registered on Concordium',
  record: 'Chain record matches',
  sender: "Registered by the issuer's account",
  'second-source': 'Second chain source agrees',
};

/**
 * Every check of SPEC.md §7, run where it is called (on the phone): nothing
 * here asks the issuer's servers whether the article is genuine.
 */
export async function verifyPacketBytes(bytes: Uint8Array, opts: VerifyOptions): Promise<Verification> {
  const now = opts.now ?? Date.now();
  const checks: Check[] = [];
  const add = (key: CheckKey, ok: boolean, detail: string, required = true) =>
    checks.push({ key, label: LABELS[key], ok, required, detail });
  const done = (extra: Partial<Verification> = {}): Verification => ({
    genuine: ALWAYS.every((k) => checks.some((c) => c.key === k && c.ok)) && checks.every((c) => c.ok || !c.required),
    checks,
    packet: null,
    record: null,
    tx: null,
    explorer: null,
    ...extra,
  });

  // 1. Signature against the issuer's published key.
  let packet: NetIdPacket;
  let issuerDoc: IssuerDocument;
  let signingKey: Uint8Array;
  try {
    const env = parseEnvelope(bytes);
    issuerDoc = await opts.resolveIssuer(env.issuer);
    if (issuerDoc.issuer !== env.issuer) throw new Error(`key file is for ${issuerDoc.issuer}, not ${env.issuer}`);
    const found = findKey(issuerDoc, env.kid, now);
    if ('error' in found) throw new Error(found.error);
    signingKey = found.key;
    if (!verifyEnvelope(env, found.key)) throw new Error(`the signature does not verify with ${env.issuer}'s key ${env.kid}`);
    packet = openPayload(env);
    add('signature', true, `Ed25519 signature by ${packet.issuer} (key ${packet.kid})`);
  } catch (e) {
    add('signature', false, (e as Error).message);
    return done({ error: (e as Error).message });
  }

  // 2. Freshness.
  const ageSec = (now - packet.iat) / 1000;
  const maxAge = opts.maxAgeSec ?? DEFAULT_MAX_AGE_SEC;
  const skew = opts.maxSkewSec ?? DEFAULT_MAX_SKEW_SEC;
  if (ageSec < -skew) add('fresh', false, `issued ${(-ageSec).toFixed(1)} s in the future (check the phone's clock)`);
  else if (ageSec > maxAge) add('fresh', false, `issued ${ageSec.toFixed(0)} s ago; a live code is at most ${maxAge} s old (a recording or screenshot?)`);
  else add('fresh', true, `issued ${Math.max(0, ageSec).toFixed(1)} s ago (limit ${maxAge} s)`);

  // A page code carries no article: only the signature and freshness (and the company checks) apply.
  if (!isArticle(packet)) return done({ packet, issuerDoc, signingKey });

  // 3. Recompute the document's fingerprint here.
  const { sha256 } = canonicalHash(packet.doc);
  const urlOk = !packet.doc.url || packet.doc.url.endsWith(`/${packet.uid}`);
  add(
    'hash',
    sha256 === packet.sha256 && urlOk,
    sha256 === packet.sha256
      ? urlOk ? `SHA-256 of the ${CANON_V2} text is ${sha256}` : `the document's url ${packet.doc.url} does not name story ${packet.uid}`
      : `the text hashes to ${sha256}, the packet claims ${packet.sha256}`,
  );

  // 4. The transaction, straight from a public Concordium service.
  const network = packet.chain.network;
  const explorer = explorerUrl(network, packet.chain.tx_hash);
  let tx: ChainTx | null = null;
  let record: ProvenanceRecord | null = null;
  try {
    tx = await lookupCcdScan(network, packet.chain.tx_hash, opts.fetch);
    if (!tx.success) throw new Error('the transaction failed on chain');
    if (!tx.dataHex) throw new Error('the transaction registers no data');
    record = decodeRecord(hexToBytes(tx.dataHex));
    add(
      'chain',
      tx.finalized,
      `${tx.finalized ? 'finalized' : 'NOT finalized'} in block ${tx.blockHeight ?? '?'} at ${tx.blockTime ?? '?'} (${tx.source}, ${network})`,
    );
  } catch (e) {
    add('chain', false, `${packet.chain.tx_hash}: ${(e as Error).message}`);
  }

  // 5. The record names this text, story, version and address.
  if (record) {
    const diffs: string[] = [];
    if (record.h !== sha256) diffs.push(`h ${record.h} ≠ ${sha256}`);
    if (record.id !== packet.uid) diffs.push(`id ${record.id} ≠ ${packet.uid}`);
    if (record.v !== packet.version) diffs.push(`v ${record.v} ≠ ${packet.version}`);
    if ((record.u ?? null) !== packet.doc.url) diffs.push(`u ${record.u} ≠ ${packet.doc.url}`);
    if (record.c !== 'v2') diffs.push(`c ${record.c} ≠ v2`);
    if (record.t !== packet.kind) diffs.push(`t ${record.t} ≠ ${packet.kind}`);
    add('record', diffs.length === 0, diffs.length ? diffs.join('; ') : `h, id, v=${record.v}, u, t, c all match`);
  } else {
    add('record', false, 'no record to compare');
  }

  // 6. Sent by the issuer's published account.
  if (tx) {
    const accounts = issuerDoc.concordium?.accounts ?? [];
    const ok = issuerDoc.concordium?.network === network && accounts.includes(tx.sender);
    add('sender', ok, ok ? `sender ${tx.sender} is ${packet.issuer}'s account` : `sender ${tx.sender} is not among ${packet.issuer}'s published ${issuerDoc.concordium?.network} accounts`);
  } else {
    add('sender', false, 'no transaction to check');
  }

  // 7. Optional: a second, independent service agrees.
  if (tx) {
    try {
      const wp = await lookupWalletProxy(network, packet.chain.tx_hash, opts.fetch);
      const ok = wp.finalized && wp.success && wp.sender === tx.sender && wp.dataHex === tx.dataHex && (!wp.blockHash || wp.blockHash === tx.blockHash);
      add('second-source', ok, ok ? `${wp.source}: same sender, data and block` : `${wp.source} disagrees (sender ${wp.sender}, status ${wp.finalized ? 'finalized' : 'not finalized'})`, false);
    } catch (e) {
      add('second-source', false, `${(e as Error).message}`, false);
    }
  }

  return done({ packet, record, tx, explorer, issuerDoc, signingKey });
}
